/**
 * Widget Dashboard « Pouls commercial » — branché sur `venteDocuments.statsResume`
 * (Lot G). Donne au vigneron l'état du pôle commerce en un coup d'œil : CA du mois,
 * reste à encaisser (alerte rust si des factures sont en retard), commandes à
 * livrer, devis en cours. Chaque chiffre est CLIQUABLE vers l'écran concerné.
 *
 * Gating : masqué tant que la permission `vente.read` n'est pas accordée (un
 * membre sans accès ventes ne voit pas ce widget). Aucune fausse donnée : l'état
 * vide s'affiche dès qu'il n'y a rien de commercial (règle dashboard).
 */

import type { VenteStatsResumeSortie } from "@champ-erp/contracts";
import { Card, Skeleton, TuileKpi, WidgetHeader } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { FileText, ReceiptText, Truck, Wallet } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useCan } from "../../auth/use-can.js";
import { trpc } from "../../lib/trpc.js";
import { formatEurosCents } from "../ventes/shared/format-montants.js";
import { ROUTES_VENTES } from "../ventes/shared/routes.js";

/** Cibles de navigation des tuiles (journal filtré ou hub). */
const CIBLE_ENCAISSER = `${ROUTES_VENTES.hub}/documents?type=facture&statut=emis`;
const CIBLE_A_LIVRER = `${ROUTES_VENTES.hub}/documents?type=commande&statut=a_livrer`;
const CIBLE_DEVIS = `${ROUTES_VENTES.hub}/documents?type=devis`;

export function WidgetPoulsCommercial() {
  const { granted, isLoading: permLoading } = useCan("vente.read");
  const navigate = useNavigate();

  // `enabled: granted` → aucune requête tant que la permission n'est pas connue
  // ou refusée (fail-closed). Les hooks restent appelés inconditionnellement.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["venteDocuments.statsResume"],
    queryFn: () => trpc.venteDocuments.statsResume.query(),
    enabled: granted,
  });

  // Sans la permission ventes, le widget n'existe pas (ni titre, ni cadre vide).
  if (permLoading || !granted) return null;

  return (
    <Card as="section" className="p-6">
      <WidgetHeader
        eyebrow="Commerce · Ventes"
        title="Pouls commercial"
        action={{
          label: "Voir les ventes",
          href: ROUTES_VENTES.hub,
          onClick: (event) => {
            event.preventDefault();
            navigate(ROUTES_VENTES.hub);
          },
        }}
      />
      {isLoading ? (
        <PoulsSkeleton />
      ) : isError || !data ? (
        <p className="rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
          Impossible de charger le suivi commercial pour l&apos;instant.
        </p>
      ) : (
        <PoulsContenu stats={data} />
      )}
    </Card>
  );
}

function PoulsContenu({ stats }: { stats: VenteStatsResumeSortie }) {
  // État vide honnête : rien de commercial du tout (ni CA ce mois, ni impayé, ni
  // commande à livrer, ni devis) → invitation, pas une grille de « 0 ».
  const rienDeCommercial =
    stats.ca_mois_ht_cents === 0 &&
    stats.nb_factures_impayees === 0 &&
    stats.nb_commandes_a_livrer === 0 &&
    stats.nb_devis_en_cours === 0;

  if (rienDeCommercial) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
          <ReceiptText size={26} />
        </div>
        <p className="text-[14px] text-champagne-800">Aucune vente ce mois.</p>
        <Link
          to={ROUTES_VENTES.nouveauDocument}
          className="text-[13px] font-medium text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
        >
          Créer un devis →
        </Link>
      </div>
    );
  }

  const aDesRetards = stats.nb_factures_en_retard > 0;

  // Chaque tuile navigue vers l'écran concerné : on branche `renderContainer`
  // sur un <Link> react-router (le TuileKpi partagé reste, lui, sans routeur).
  // 2 colonnes À TOUTES LES LARGEURS, et non 4 à partir de `lg`.
  // Les media queries réagissent au VIEWPORT, pas au conteneur : à partir de
  // 1100 px le dashboard passe en 2 colonnes, donc ce widget se retrouve dans
  // une colonne de ~500 px — 4 tuiles y font 116 px, alors qu'un montant en
  // 22 px en demande 121. Les euros y étaient rognés depuis le Lot H.
  // En 2 colonnes, chaque tuile fait ~244 px : confortable partout.
  return (
    <div className="grid grid-cols-2 gap-3">
      <TuileKpi
        icon={<Wallet size={16} />}
        titre="CA du mois"
        valeur={formatEurosCents(stats.ca_mois_ht_cents)}
        detail="HT · factures émises"
        renderContainer={({ className, children }) => (
          <Link to={ROUTES_VENTES.hub} className={className}>
            {children}
          </Link>
        )}
      />
      <TuileKpi
        icon={<ReceiptText size={16} />}
        titre="À encaisser"
        valeur={formatEurosCents(stats.montant_impaye_cents)}
        detail={
          stats.nb_factures_impayees > 0
            ? `${stats.nb_factures_impayees} facture${stats.nb_factures_impayees > 1 ? "s" : ""}`
            : "rien à encaisser"
        }
        alerte={aDesRetards ? `${stats.nb_factures_en_retard} en retard` : undefined}
        renderContainer={({ className, children }) => (
          <Link to={CIBLE_ENCAISSER} className={className}>
            {children}
          </Link>
        )}
      />
      <TuileKpi
        icon={<Truck size={16} />}
        titre="À livrer"
        valeur={String(stats.nb_commandes_a_livrer)}
        detail={`commande${stats.nb_commandes_a_livrer > 1 ? "s" : ""}`}
        renderContainer={({ className, children }) => (
          <Link to={CIBLE_A_LIVRER} className={className}>
            {children}
          </Link>
        )}
      />
      <TuileKpi
        icon={<FileText size={16} />}
        titre="Devis en cours"
        valeur={String(stats.nb_devis_en_cours)}
        detail="devis"
        renderContainer={({ className, children }) => (
          <Link to={CIBLE_DEVIS} className={className}>
            {children}
          </Link>
        )}
      />
    </div>
  );
}

/** Squelette de chargement : 4 tuiles KPI. */
function PoulsSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3" aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rect" height={104} />
      ))}
    </div>
  );
}
